/* That's Classic — reusable enquiry + consent pop-up.
   Triggers: links to "#enquire", [data-tc-enquire], or window.TCEnquiry.open({...}).
   The form is a normal Shopify contact form POST, so it works with Shopify's bot
   protection and lands in the store inbox. After posting back, the pop-up reopens
   to show the success message (or errors). */
(() => {
  const modal = document.querySelector('[data-tc-modal]');
  const store = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };

  const pageName = () => (location.pathname === '/' ? 'Homepage' : document.title.split(/ [|–-] /)[0].trim());

  const configure = (form, { subject, url, topic, car } = {}) => {
    if (!form) return;
    const field = (n) => form.querySelector(`[data-tc-field="${n}"]`);
    if (field('subject')) field('subject').value = subject || pageName();
    if (field('url')) field('url').value = url || location.href.split(/[?#]/)[0];
    // Car enquiries get the car options, everything else the general ones
    const set = car ? 'car' : 'general';
    form.querySelectorAll('[data-tc-topic-wrap]').forEach((wrap) => {
      const on = wrap.dataset.tcTopicWrap === set;
      wrap.hidden = !on;
      const select = wrap.querySelector('select');
      select.disabled = !on;
      if (!on) return;
      const want = topic || (car && /^Similar to/i.test(subject || '') ? select.options[1]?.value : '');
      const match = want && [...select.options].find((o) => o.value.toLowerCase() === want.toLowerCase());
      select.value = match ? match.value : select.options[0]?.value;
    });
  };

  const open = (opts = {}) => {
    if (!modal) {
      // No pop-up on this page (e.g. the contact page): jump to the inline form
      const inline = document.querySelector('#enquire, [data-tc-form]');
      if (inline) {
        configure(inline.matches('[data-tc-form]') ? inline : inline.querySelector('[data-tc-form]'), opts);
        inline.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    }
    configure(modal.querySelector('[data-tc-form]'), opts);
    const title = opts.title || modal.dataset.defaultTitle || 'Get in touch';
    const titleEl = modal.querySelector('[data-tc-modal-title]');
    if (titleEl) titleEl.textContent = title;
    store.set('tc-enquiry-title', title);
    if (!modal.open) modal.showModal();
    document.documentElement.classList.add('tc-modal-open');
    modal.querySelector('select:not([disabled]), input:not([type=hidden])')?.focus({ preventScroll: true });
    return true;
  };

  const isHashTrigger = (a) => a.tagName === 'A' && a.hash === '#enquire';

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-tc-enquire], a[href*="#enquire"]');
    if (!t || (t.tagName === 'A' && !t.hasAttribute('data-tc-enquire') && !isHashTrigger(t))) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    const d = t.dataset;
    const opened = open({
      subject: d.tcSubject || (isHashTrigger(t) ? `${t.textContent.trim()} (${pageName()})` : undefined),
      title: d.tcTitle,
      topic: d.tcTopic,
      car: d.tcCar === 'true',
      url: d.tcUrl,
    });
    if (opened) e.preventDefault();
  });

  if (modal) {
    modal.querySelector('[data-tc-modal-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
    modal.addEventListener('close', () => document.documentElement.classList.remove('tc-modal-open'));

    // Posted back from Shopify: show success / errors in the pop-up
    const state = modal.querySelector('[data-tc-form-state]');
    // Both forms are Shopify contact forms, so both render the posted state; the URL hash
    // (#<form id>, kept through Shopify's redirect) tells us which one was actually sent.
    const posted = location.hash.length > 1 ? document.getElementById(location.hash.slice(1)) : null;
    const inlineHandled = !!posted && posted.matches('[data-tc-form]') && !modal.contains(posted);
    const notContact = /customer_posted/.test(location.search) || (!!posted && !posted.matches('[data-tc-form]'));
    if (state && !inlineHandled && !notContact) {
      modal.showModal();
      document.documentElement.classList.add('tc-modal-open');
      const titleEl = modal.querySelector('[data-tc-modal-title]');
      if (state.dataset.tcFormState === 'success') {
        modal.classList.add('is-success');
        if (titleEl) titleEl.textContent = state.querySelector('.tc-form__success-title')?.textContent || 'Thank you.';
      } else if (titleEl) titleEl.textContent = store.get('tc-enquiry-title') || titleEl.textContent;
    }
  }

  // Deep links: /any-page#enquire opens the pop-up; ?enquiry=Name prefills the subject
  const params = new URLSearchParams(location.search);
  if (params.get('enquiry')) document.querySelectorAll('[data-tc-form]').forEach((f) => configure(f, { subject: params.get('enquiry'), car: true }));
  if (location.hash === '#enquire' && modal && !modal.open) open({ subject: params.get('enquiry') || undefined });

  // ---- Make: every form also sends its details to the Make webhook (Theme settings → "Make webhook"),
  // which creates/updates the Shopify customer with consent + tags. The store email still goes out as
  // normal afterwards, so nothing is lost if Make is down. Valuation forms (tc-valuation.js) add photos.
  const slug = (v) => (v || '').toString().toLowerCase().normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 60);
  const fieldsOf = (form) => {
    const out = {};
    new FormData(form).forEach((v, k) => {
      const key = k.replace(/^contact\[(.*)\]$/, '$1');
      if (!['form_type', 'utf8'].includes(key) && typeof v === 'string') out[key] = v; // later values win (consent No → Yes)
    });
    return out;
  };
  const tagsFor = (form, f) => {
    const name = form.dataset.tcFormName || '';
    if (form.matches('[data-tc-newsletter]')) {
      const t = ['newsletter', form.dataset.tcSource || 'footer-signup'];
      if (f.Interest) t.push('interest-' + slug(f.Interest));
      return t;
    }
    if (form.matches('[data-tc-valform]')) return ['valuation-enquiry', 'valuation-' + slug((f['Valuation type'] || 'general').replace(/ valuation$/i, ''))];
    const t = ['enquiry-form', 'form-' + slug(name.replace(/^Inline form: /, ''))];
    if (f.Topic) t.push('topic-' + slug(f.Topic));
    if (location.pathname.startsWith('/products/')) t.push('car-' + location.pathname.split('/products/')[1].split(/[/?#]/)[0]);
    return t;
  };
  window.TCMake = {
    payload(form, extra = {}) {
      const f = fieldsOf(form);
      if (form.matches('[data-tc-newsletter]')) { f['Email marketing consent'] = 'Yes'; f['SMS marketing consent'] = f['SMS marketing consent'] || 'No'; }
      return { ...f, form: form.dataset.tcFormName || (form.matches('[data-tc-newsletter]') ? 'Footer newsletter' : 'Enquiry'), tags: tagsFor(form, f).join(', '), page: location.href, submitted_at: new Date().toISOString(), photos: [], ...extra };
    },
    async send(endpoint, data, wait = 2500) {
      try {
        const body = new URLSearchParams({ payload: JSON.stringify(data) });
        const big = (data.photos || []).length > 0;
        await Promise.race([
          fetch(endpoint, { method: 'POST', mode: 'no-cors', body, keepalive: !big }),
          new Promise((r) => setTimeout(r, big ? 20000 : wait)),
        ]);
      } catch (x) { /* Make unreachable: the store email still goes out */ }
    },
  };
  // Runs in the capture phase (before Shopify's own CAPTCHA/bot-protection handler on the form):
  // pause the first submit, send to Make, then resubmit with requestSubmit() so Shopify's handler
  // runs normally and adds its token. Valuation forms are handled the same way in tc-valuation.js.
  window.TCMake.resubmit = (form) => {
    const btn = form.querySelector('[type=submit]');
    if (btn) { btn.disabled = false; }
    if (form.requestSubmit) form.requestSubmit(btn || undefined); else HTMLFormElement.prototype.submit.call(form);
  };
  window.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-tc-form]:not([data-tc-valform]), form[data-tc-newsletter]')) return;
    const endpoint = form.dataset.endpoint;
    if (!endpoint || form.dataset.tcSent) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!form.reportValidity()) return;
    form.dataset.tcSent = '1';
    const btn = form.querySelector('[type=submit]');
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    await window.TCMake.send(endpoint, window.TCMake.payload(form));
    window.TCMake.resubmit(form);
  }, true);

  // Fallback when no Make webhook is set: submit a real hidden Shopify customer form (#tc-optin-form)
  // into a hidden iframe so Shopify's own bot protection runs on it. Never blocks the enquiry.
  const optin = document.getElementById('tc-optin-form');
  const optinFrame = document.querySelector('iframe[name="tc-optin-frame"]');
  document.querySelectorAll('[data-tc-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      if (form.dataset.endpoint) return; // Make handles consent
      if (!optin || !optinFrame || form.dataset.tcBridged || !form.querySelector('[data-tc-consent="email"]')?.checked) return;
      e.preventDefault();
      form.dataset.tcBridged = '1';
      const btn = form.querySelector('[type=submit]');
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      const name = (form.querySelector('[name="contact[Name]"]')?.value || '').trim().split(/\s+/);
      const set = (n, v) => { const el = optin.querySelector(`[name="contact[${n}]"]`); if (el) el.value = v; };
      set('email', form.querySelector('[name="contact[email]"]').value);
      set('first_name', name[0] || '');
      set('last_name', name.slice(1).join(' '));
      optin.target = 'tc-optin-frame';
      await new Promise((resolve) => {
        optinFrame.addEventListener('load', () => resolve(), { once: true });
        setTimeout(resolve, 5000);
        if (optin.requestSubmit) optin.requestSubmit(); else optin.submit();
      });
      if (form.requestSubmit) form.requestSubmit(); else HTMLFormElement.prototype.submit.call(form);
    });
  });

  // Footer newsletter posted back: bring the confirmation into view
  const nlOk = document.querySelector('[data-tc-nl-success]');
  if (nlOk) { nlOk.scrollIntoView({ block: 'center' }); nlOk.focus({ preventScroll: true }); }

  // [data-tc-model] links (e.g. "Find me one"): prefill the inline brief and jump to it
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-tc-model]');
    if (!t) return;
    const input = document.querySelector('[data-tc-field="model"]');
    if (!input) return;
    e.preventDefault();
    input.value = t.dataset.tcModel;
    (document.getElementById('enquiry') || input).scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    input.focus({ preventScroll: true });
  });

  // "Cookie preferences" links reopen Shopify's consent banner
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-tc-cookie-prefs]');
    if (!t) return;
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.showPreferences === 'function') { e.preventDefault(); cp.showPreferences(); }
    else { e.preventDefault(); location.href = '/pages/cookie-policy'; }
  });

  window.TCEnquiry = { open, close: () => modal?.close() };
})();
